import { z } from "zod";
// ---------------------------------------------------------------------------
// « Papillon », manche bleue (GA-4) — LE FICHIER DES CARTES.
//
// ⚠️ JURIDIQUE — À LIRE AVANT DE TOUCHER À CE FICHIER.
// Le jeu est sous LICENCE de l'éditeur ; Henri déclare la détenir et a tranché
// le 25 juillet 2026 : on emploie les NOMS OFFICIELS (le jeu, la manche, les
// cartes spéciales). En revanche AUCUN asset de l'éditeur n'est embarqué — ils
// ne sont pas au dépôt : les cartes sont DESSINÉES MAISON (SVG, deux couleurs).
//
// Ce fichier est le POINT UNIQUE de tout ce qui est « du jeu » : noms officiels,
// composition du paquet, barème, bornes de valeurs, et l'emplacement réservé à
// la mention de crédit de l'éditeur. Le jour où la licence écrite et les assets
// arrivent, le branchement reste LOCAL : ce fichier (+ les glyphes SVG de
// `apps/mobile/src/screens/games/PapillonCards.tsx`). Le reducer, le protocole
// et le plateau ne changent pas.
//
// Ce qui manque encore côté licence (cf. implementation_plan.md § Licence) :
//   1. la portée ÉCRITE — adaptation numérique + distribution App Store /
//      Play Store (une licence sur le jeu physique ne les couvre pas d'office) ;
//   2. les assets officiels (faces, dos, logo) ;
//   3. la mention de crédit exigée et son emplacement → `PAPILLON_CREDIT`.
// ---------------------------------------------------------------------------
/** Nom officiel du jeu (nom propre : identique fr/en). */
export const PAPILLON_GAME_NAME = "Papillon";
/** La seule manche au programme de ce lot (décision d'Henri). */
export const PAPILLON_ROUND_NAME = "manche bleue";
/**
 * Mention de crédit exigée par l'éditeur (« sous licence … »).
 *
 * `null` = pas encore fournie par Henri → rien ne s'affiche. Dès qu'elle porte
 * un texte, elle apparaît EN PIED DU PANNEAU « Règles » du plateau
 * (`PapillonGame.tsx`), accessible depuis n'importe quelle partie. Si l'éditeur
 * impose un autre emplacement (écran de fin, tuile du hub, mentions légales),
 * c'est d'ici qu'on le branche — pas ailleurs.
 */
export const PAPILLON_CREDIT = null;
// ---------------------------------------------------------------------------
// Nature des cartes
// ---------------------------------------------------------------------------
/**
 * - `num`        : carte chiffrée (le tapis et les ramassages vivent d'elles) ;
 * - `op`         : atout d'opération (`−` `×` `÷`) — se dépense, ne se pose pas ;
 * - `joker`      : « Couteau Suisse » — remplace les valeurs 0 à 11 ;
 * - `papillon`   : « Papillon » — rafle tout le tapis ;
 * - `chrysalide` : « Chrysalide » — inverse le sens du jeu ;
 * - `sens`       : « Sens » — détermine la direction.
 */
export const PapillonCardKindSchema = z.enum([
    "num",
    "op",
    "joker",
    "papillon",
    "chrysalide",
    "sens",
]);
/** Les quatre opérateurs de la spec. Écriture ASCII : `*` et `/` (le signe
 *  d'affichage, lui, est dans `PAPILLON_OP_SIGNS`). */
export const PapillonOpSchema = z.enum(["+", "-", "*", "/"]);
/** Signes affichés — jamais `*` ni `/` à l'écran (kit graphique). */
export const PAPILLON_OP_SIGNS = {
    "+": "+",
    "-": "−",
    "*": "×",
    "/": "÷",
};
/**
 * Noms des cartes. Les quatre spéciales portent les noms OFFICIELS de
 * l'éditeur (licence). `num` et `op` sont des libellés MAISON : les noms
 * officiels des cartes chiffrées et des atouts ne nous ont pas été fournis —
 * ils se remplacent ici, et nulle part ailleurs.
 */
export const PAPILLON_CARD_NAMES = {
    num: "Chiffre",
    op: "Atout",
    joker: "Couteau Suisse",
    papillon: "Papillon",
    chrysalide: "Chrysalide",
    sens: "Sens",
};
/** Cartes spéciales — 10 points chacune à qui les ramasse. */
export const PAPILLON_SPECIAL_KINDS = [
    "joker",
    "papillon",
    "chrysalide",
    "sens",
];
export function isPapillonSpecialKind(kind) {
    return PAPILLON_SPECIAL_KINDS.includes(kind);
}
// ---------------------------------------------------------------------------
// Barème de la manche bleue
// ---------------------------------------------------------------------------
/** Chaque carte spéciale ramassée. */
export const PAPILLON_SPECIAL_POINTS = 10;
/** Bonus au plus grand nombre de cartes ramassées — égalité : personne. */
export const PAPILLON_MOST_CARDS_POINTS = 10;
/**
 * LA règle contre-intuitive (à expliquer à l'écran, sinon elle passe pour un
 * bug) : qui finit la manche SANS AUCUN POINT reçoit 10 points DE CHAQUE
 * adversaire.
 */
export const PAPILLON_ZERO_BOUNTY = 10;
// ---------------------------------------------------------------------------
// Composition du paquet — les valeurs officielles ne nous ont pas été
// communiquées : ce qui suit est une composition MAISON cohérente avec la spec
// (Couteau Suisse « remplace les valeurs 0 à 11 » ⇒ des chiffres 0 à 11). Elle
// se corrige ICI, et le reducer suit sans être touché.
// ---------------------------------------------------------------------------
/** Plus petite / plus grande valeur chiffrée (bornes du Couteau Suisse). */
export const PAPILLON_MIN_VALUE = 0;
export const PAPILLON_MAX_VALUE = 11;
/** Cartes en main au départ, et complétées après chaque coup. */
export const PAPILLON_HAND_SIZE = 5;
/** Cartes CHIFFRÉES posées sur le tapis à l'ouverture. */
export const PAPILLON_TAPIS_START = 4;
/**
 * 34 cartes : 24 chiffrées (0-11 en double), 6 atouts (`−` `×` `÷` en double),
 * 4 spéciales.
 *
 * Pas d'atout `+` : la somme (« cumuler », règle 2 de la spec) est GRATUITE —
 * un atout `+` serait une carte morte. Le protocole accepte tout de même
 * `op: "+"` comme écriture explicite de cette règle, sans rien consommer.
 */
export const PAPILLON_DECK = [
    ...Array.from({ length: PAPILLON_MAX_VALUE - PAPILLON_MIN_VALUE + 1 }, (_, i) => ({
        kind: "num",
        value: PAPILLON_MIN_VALUE + i,
        op: null,
        count: 2,
    })),
    { kind: "op", value: null, op: "-", count: 2 },
    { kind: "op", value: null, op: "*", count: 2 },
    { kind: "op", value: null, op: "/", count: 2 },
    { kind: "joker", value: null, op: null, count: 1 },
    { kind: "papillon", value: null, op: null, count: 1 },
    { kind: "chrysalide", value: null, op: null, count: 1 },
    { kind: "sens", value: null, op: null, count: 1 },
];
export const PAPILLON_DECK_SIZE = PAPILLON_DECK.reduce((n, e) => n + e.count, 0);
/**
 * Cartes du tapis réalisant un ramassage GRATUIT avec `card`, ou `null` si
 * aucun n'est possible. Une carte du résultat = égalité ; deux ou plus = somme.
 */
export function findPapillonFreeCapture(tapis, card) {
    const valued = tapis.filter((c) => c.value != null);
    if (valued.length === 0)
        return null;
    // Le Couteau Suisse prend n'importe quelle valeur de la plage : n'importe
    // quelle carte chiffrée du tapis lui suffit (égalité).
    if (card.kind === "joker")
        return [valued[0]];
    if (card.kind !== "num" || card.value == null)
        return null;
    const target = card.value;
    // Sommes atteignables, avec le sous-ensemble qui les réalise (un
    // représentant par somme suffit : c'est la DP subset-sum classique).
    const reach = new Map();
    for (const c of valued) {
        const v = c.value;
        if (v > PAPILLON_MAX_VALUE)
            continue;
        const additions = [];
        if (!reach.has(v))
            additions.push([v, [c]]);
        for (const [sum, subset] of reach) {
            const next = sum + v;
            if (next > PAPILLON_MAX_VALUE || reach.has(next))
                continue;
            additions.push([next, [...subset, c]]);
        }
        for (const [sum, subset] of additions)
            if (!reach.has(sum))
                reach.set(sum, subset);
    }
    return reach.get(target) ?? null;
}
//# sourceMappingURL=papillonCards.js.map